import i18next from 'i18next';
import { OneIdEnvironment } from '../../hooks/useOneIdEnvironment';
import {
  generateRevenueUrl,
  RevenueNavigationPrimaryItem,
  RevenueNavigationResponse,
  RevenueNavigationSecondaryItem,
} from '../../hooks/useRevenueNavigation';
import {
  addPlatformServiceSectionsToNavigationItems,
  REVENUE_PLATFORM_SERVICE_SECTIONS,
} from '../../platformServiceNavigation';
import { NavigationItem } from '../../types';
import { mapIdToIcon } from '../utils/mappers';
import { BaseNavigationTransformer } from './base-navigation-transformer';

export class RevenueNavigationTransformer extends BaseNavigationTransformer {
  private revenuePrefix: OneIdEnvironment['revenuePrefix'];
  constructor(revenuePrefix: OneIdEnvironment['revenuePrefix'] = '/revenue') {
    super();
    this.revenuePrefix = revenuePrefix;
  }

  // transform(revenueData: RevenueNavigationResponse): NavigationItem[] {
  //   if (!revenueData?.items?.length) {
  //     return [];
  //   }

  //   return revenueData.items.map((primaryItem) =>
  //     this.transformPrimaryItem(primaryItem),
  //   );
  // }

  transform(revenueData: RevenueNavigationResponse): {
    secondaryItems: NavigationItem[];
    bottomItems: NavigationItem[];
  } {
    if (!revenueData?.items?.length) {
      return {
        secondaryItems: [],
        bottomItems: [],
      };
    }

    const secondaryItems = revenueData.items.map((primaryItem) =>
      this.transformPrimaryItem(primaryItem),
    );

    return {
      secondaryItems: addPlatformServiceSectionsToNavigationItems(
        secondaryItems,
        REVENUE_PLATFORM_SERVICE_SECTIONS,
      ),
      bottomItems: [
        this.createFeedbackItem(),
        {
          label: i18next.t('GlobalNav.Sections.Help'),
          id: 'Zuora/HelpAndTraining',
          icon: 'help',
          popover: this.createHelpPopover(
            i18next.t('GlobalNav.Sections.Help'),
            false,
          ),
        },
      ],
    };
  }

  private transformPrimaryItem(
    primaryItem: RevenueNavigationPrimaryItem,
  ): NavigationItem {
    const item: NavigationItem = {
      label: primaryItem.display_value,
      id: primaryItem.id,
      icon: mapIdToIcon(primaryItem.id),
      href: generateRevenueUrl({
        prefix: this.revenuePrefix,
        primaryItem,
      }),
    };

    if (primaryItem.first_level?.length) {
      this.addExpandableContent(item, primaryItem);
    }

    return item;
  }

  private addExpandableContent(
    item: NavigationItem,
    primaryItem: RevenueNavigationPrimaryItem,
  ): void {
    item.expandable = true;

    const { regularItems, tertiaryItems } = this.categorizeSecondaryItems(
      primaryItem.first_level,
    );

    item.popover = {
      label: primaryItem.display_value,
      id: `${primaryItem.id}-popover`,
      items: this.transformRegularItems(primaryItem, regularItems),
    };

    this.addTertiaryContent(item, primaryItem, tertiaryItems);
  }

  private categorizeSecondaryItems(
    secondaryItems: RevenueNavigationSecondaryItem[],
  ) {
    return {
      regularItems: secondaryItems.filter((item) => !item.second_level?.length),
      tertiaryItems: secondaryItems.filter((item) => item.second_level?.length),
    };
  }

  private transformRegularItems(
    primaryItem: RevenueNavigationPrimaryItem,
    regularItems: RevenueNavigationSecondaryItem[],
  ) {
    return regularItems.map((secondaryItem) => ({
      label: secondaryItem.display_value,
      id: secondaryItem.id,
      href: generateRevenueUrl({
        prefix: this.revenuePrefix,
        primaryItem,
        secondaryItem,
      }),
      chip: '',
      pinnable: true,
    }));
  }

  private addTertiaryContent(
    item: NavigationItem,
    primaryItem: RevenueNavigationPrimaryItem,
    tertiaryItems: RevenueNavigationSecondaryItem[],
  ): void {
    tertiaryItems.map((tertiaryItem) => {
      this.addSecondaryTertiary(item, primaryItem, tertiaryItem);
    });
  }

  private addSecondaryTertiary(
    item: NavigationItem,
    primaryItem: RevenueNavigationPrimaryItem,
    tertiaryItem: RevenueNavigationSecondaryItem,
  ): void {
    if (!item.popover) return;

    if (!item.popover.additionalItems) {
      item.popover.additionalItems = [];
    }

    item.popover.additionalItems.push({
      label: tertiaryItem.display_value,
      id: `${tertiaryItem.id}-popover`,
      items: tertiaryItem.second_level
        .map((tertiary) => ({
          label: tertiary.display_value,
          id: tertiary.id,
          href: generateRevenueUrl({
            prefix: this.revenuePrefix,
            primaryItem,
            secondaryItem: tertiaryItem,
            tertiaryItem: tertiary,
          }),
          chip: '',
          pinnable: true,
        }))
        .sort((a, b) => {
          const aItem = tertiaryItem.second_level.find(
            (item) => item.id === a.id,
          );
          const bItem = tertiaryItem.second_level.find(
            (item) => item.id === b.id,
          );
          return (aItem?.seq || 0) - (bItem?.seq || 0);
        }),
    });
  }
}
